import * as React from 'react'
import { useEffect } from 'react'

import { PartnerClient } from './types'
import { ColumnsType } from 'antd/lib/table'
import { Flex, Table, Tag, Tooltip, Typography } from 'antd'
import { usePartnerClientsList } from './hooks'
import CIcon from '@coreui/icons-react'
import { cilCheckCircle, cilXCircle } from '@coreui/icons'
import { useUnit } from 'effector-react'
import { $countriesList } from '../../../../../features/dictionaries/model'
import { transformCountriesToOptions } from '../../../../../libs/formatCountries'
import { useUrlFilters } from '../../../../../libs/useUrlFilters'
import { PartnerClientFiltersPanel } from './filtersPanel'
import { path } from '../../../../../libs'
import { CrownOutlined, ExperimentOutlined } from '@ant-design/icons'

const { Text } = Typography

const FILTER_CONFIG = {
  clientId: { type: 'string' as const },
  email: { type: 'string' as const },
  sumCommissions: { type: 'string' as const },
  sumLots: { type: 'string' as const },
  sumCashback: { type: 'string' as const },
  verified: { type: 'boolean' as const },
  hasDeposit: { type: 'boolean' as const },
  country: { type: 'array' as const },
  dateFrom: { type: 'string' as const },
  dateTo: { type: 'string' as const },
  page: { type: 'string' as const, default: '1', pagination: true },
  pageSize: { type: 'string' as const, default: '10', pagination: true },
  sortField: { type: 'string' as const, default: 'clientId', pagination: true },
  sortOrder: { type: 'string' as const, default: 'descend', pagination: true },
}

export const PartnerClientsWidget = () => {
  return (
    <div>
      <PartnerClientList />
    </div>
  )
}

const PartnerClientList = () => {
  return (
    <div>
      <PartnerClientsTable />
    </div>
  )
}

const PartnerClientsTable = () => {
  const {
    partnerClients,
    totalCountOfPartnerClients,
    loading,
    onChangePartnerClientsList,
  } = usePartnerClientsList()
  const countriesList = useUnit($countriesList)

  const { filters, setFilters, resetFilters } = useUrlFilters(FILTER_CONFIG)

  const page = Number(filters.page) || 1
  const pageSize = Number(filters.pageSize) || 10
  const orderBy = filters.sortField || 'clientId'
  const orderDirection = filters.sortOrder === 'ascend' ? '' : '-'

  const countryOptions = React.useMemo(
    () =>
      transformCountriesToOptions(countriesList).map(
        (c: { text: string; value: string }) => ({
          label: c.text,
          value: c.value,
        }),
      ),
    [countriesList],
  )

  const countryNameByCode = React.useMemo(() => {
    const map: Record<string, string> = {}
    countryOptions.forEach((c) => {
      map[c.value] = c.label as string
    })
    return map
  }, [countryOptions])

  useEffect(() => {
    const searchText: Record<string, any> = {}
    if (filters.clientId) searchText['clientId'] = filters.clientId
    if (filters.email) searchText['client.email'] = filters.email
    if (filters.sumCommissions)
      searchText['sumCommissions'] = filters.sumCommissions
    if (filters.sumLots) searchText['sumLots'] = filters.sumLots
    if (filters.sumCashback) searchText['sumCashback'] = filters.sumCashback
    if (filters.dateFrom && filters.dateTo) {
      searchText['attachedAt'] = [filters.dateFrom, filters.dateTo]
    }

    onChangePartnerClientsList({
      limit: pageSize,
      page,
      orderBy,
      orderDirection,
      searchText,
      filters: {
        ...(filters.verified !== undefined && { verified: filters.verified }),
        ...(filters.hasDeposit !== undefined && {
          hasDeposit: filters.hasDeposit,
        }),
        ...(filters.country &&
          filters.country.length > 0 && { country: filters.country }),
      },
    })
  }, [filters, page, pageSize, orderBy, orderDirection])

  const columns: ColumnsType<PartnerClient> = [
    {
      title: 'ID',
      sorter: true,
      defaultSortOrder: 'descend',
      render: (item: PartnerClient) => (
        <Flex align="center" gap={4} wrap="wrap" justify="center">
          <a
            href={path.clients.details(String(item.clientId))}
            target="_blank"
            rel="noreferrer"
          >
            {item.clientId}
          </a>
          {item.client.isTest && (
            <Tag
              icon={<ExperimentOutlined />}
              color="purple"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Test
            </Tag>
          )}
          {item.client.isPremium && (
            <Tag
              icon={<CrownOutlined />}
              color="gold"
              style={{ fontSize: 10, margin: 0, padding: '0 4px' }}
            >
              Premium
            </Tag>
          )}
        </Flex>
      ),
    },
    {
      title: 'E-mail',
      dataIndex: ['client', 'email'],
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Verification',
      dataIndex: ['client', 'verified'],
      render: (item) => (
        <div className="text-center">
          {item ? (
            <CIcon className="text-success" icon={cilCheckCircle} />
          ) : (
            <CIcon className="text-danger" icon={cilXCircle} />
          )}
        </div>
      ),
    },
    {
      title: 'Has deposit',
      dataIndex: ['client', 'hasDeposit'],
      render: (item) => (
        <div className="text-center">
          {item ? (
            <CIcon className="text-success" icon={cilCheckCircle} />
          ) : (
            <CIcon className="text-danger" icon={cilXCircle} />
          )}
        </div>
      ),
    },
    {
      title: 'Country',
      dataIndex: ['client', 'country'],
      render: (item) => (
        <Tooltip title={item}>
          <Text style={{ fontSize: 14 }}>
            {countryNameByCode[item] || item}
          </Text>
        </Tooltip>
      ),
    },
    {
      title: 'Commissions',
      dataIndex: 'sumCommissions',
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Lots',
      dataIndex: 'sumLots',
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Cashback',
      dataIndex: 'sumCashback',
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Deposit',
      dataIndex: 'sumDeposits',
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Withdrawal',
      dataIndex: 'sumWithdrawals',
      sorter: true,
      render: (item) => <>{item}</>,
    },
    {
      title: 'Attached At',
      dataIndex: 'attachedAt',
      sorter: true,
    },
  ]

  return (
    <div>
      <PartnerClientFiltersPanel
        filters={filters}
        setFilters={setFilters}
        resetFilters={resetFilters}
        countryOptions={countryOptions}
      />

      <Table
        dataSource={partnerClients}
        pagination={{
          current: page,
          pageSize,
          showSizeChanger: true,
          pageSizeOptions: [10, 25, 50, 100],
          total: totalCountOfPartnerClients,
          showTotal: (t, [f, to]) => `${f}\u2013${to} of ${t}`,
          position: ['topRight', 'bottomRight'],
        }}
        onChange={(pagination, _filters, sorter) => {
          const updates: Record<string, any> = {}
          if (pagination.current)
            updates.page =
              pagination.current === 1 ? undefined : String(pagination.current)
          if (pagination.pageSize && pagination.pageSize !== pageSize) {
            updates.pageSize = String(pagination.pageSize)
            updates.page = undefined
          }
          const singleSorter = Array.isArray(sorter) ? sorter[0] : sorter
          if (singleSorter?.field) {
            updates.sortField =
              singleSorter.field === 'clientId' ? undefined : singleSorter.field
            updates.sortOrder =
              singleSorter.order === 'descend' ? undefined : 'ascend'
          }
          setFilters(updates as any)
        }}
        columns={columns}
        bordered={true}
        scroll={{ x: true }}
        loading={loading}
        rowKey="partnerClientId"
        size="small"
      />
    </div>
  )
}
